import React, { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useLocalization } from "@fluent/react";
import { Search } from "react-feather";

import Tabs from "../tabs/Tab";
import ThreadSearch from "./ThreadSearch";
import ThreadList from "./ThreadList";

type Thread = {
  threadId: string;
  subject: string;
  updatedAt: string;
  isRead?: boolean;
};

type ThreadResult = {
  threads: Thread[];
};

type LoadAllThreadsResult = {
  startingThread: Promise<ThreadResult>;
  endingThread: Promise<ThreadResult>;
};

type TabbedThreadListProps = {
  selectedThreadId: string | undefined;
  setSelectedThreadId: React.Dispatch<React.SetStateAction<string | undefined>>;
  loadAllThreads: () => LoadAllThreadsResult;
};

type SearchAction = {
  label: string;
  fancyLabel: ReactNode;
  className: string;
  onSubmit: (callback: () => void) => void;
};

type TabPane = {
  header: {
    text: string;
    actions: SearchAction[];
  };
  render: () => ReactNode;
};

export default function TabbedThreadList({
  selectedThreadId,
  setSelectedThreadId,
  loadAllThreads,
}: TabbedThreadListProps) {
  const { l10n } = useLocalization();
  const [recentThreads, setRecentThreads] = useState<Thread[] | null>(null);
  const [olderThreads, setOlderThreads] = useState<Thread[] | null>(null);
  const [searchOpened, setSearchOpened] = useState(false);

  useEffect(() => {
    const threads = loadAllThreads();
    threads.startingThread.then((result) => {
      setRecentThreads(result.threads);
      if (result.threads.length > 0) {
        setSelectedThreadId(
          (threadId) => threadId || result.threads[0].threadId
        );
      }
    });
    threads.endingThread.then((result) => setOlderThreads(result.threads));
  }, []);

  useEffect(() => {
    const threads = loadAllThreads();
    threads.startingThread.then((result) => {
      setRecentThreads(result.threads);
    });
    threads.endingThread.then((result) => setOlderThreads(result.threads));
  }, [selectedThreadId]);

  function renderRecentThreads() {
    return (
      <ThreadList
        threads={recentThreads}
        selectedThreadId={selectedThreadId}
        onThreadSelected={(newSelectedThreadId?: string) =>
          setSelectedThreadId(newSelectedThreadId)
        }
      />
    );
  }

  function renderOlderThreads() {
    return (
      <ThreadList
        threads={olderThreads}
        selectedThreadId={selectedThreadId}
        onThreadSelected={(newSelectedThreadId?: string) =>
          setSelectedThreadId(newSelectedThreadId)
        }
      />
    );
  }

  const searchActions: SearchAction[] = [
    {
      label: l10n.getString("message-action-search"),
      fancyLabel: <Search size={18} />,
      className: "ui button message-icon-button",
      onSubmit: (callback) => {
        setSearchOpened(true);
        callback();
      },
    },
  ];

  const tabPanes: TabPane[] = [
    {
      header: {
        text: l10n.getString("message-tab-recent"),
        actions: searchActions,
      },
      render: renderRecentThreads,
    },
    {
      header: {
        text: l10n.getString("message-tab-older"),
        actions: searchActions,
      },
      render: renderOlderThreads,
    },
  ];

  return (
    <div className="tabbed-thread-list">
      {searchOpened ? (
        <ThreadSearch
          closeSearch={() => setSearchOpened(false)}
          selectedThreadId={selectedThreadId}
          setSelectedThreadId={setSelectedThreadId}
        />
      ) : (
        <Tabs panes={tabPanes} />
      )}
    </div>
  );
}
